import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import type { ASPSP } from '@/types/openpayments/bank';

const BANK_CATALOG_QUERY_KEY = ['bank-info', 'SE'] as const;

/**
 * The one rule for what a bank is called in the consultant app (FIN-802, EC7): the bank
 * catalog's name for the account's BIC, falling back to the stored bank name. Every screen that
 * names an account uses this, so one account never shows under two names on the same screen.
 * The catalog is fetched once and shared by every caller.
 */
export function useBankCatalog() {
  const { data } = useQuery({
    queryKey: BANK_CATALOG_QUERY_KEY,
    queryFn: () => ServiceRegistry.getBankInfoService().listBanksByCountry('SE'),
    staleTime: 60 * 60 * 1000,
  });

  const byBic = useCallback(
    (bic: string | undefined): ASPSP | undefined => (bic ? data?.find((bank) => bank.bicFi === bic) : undefined),
    [data],
  );

  const getBankDisplayName = useCallback(
    (bic: string | undefined, bankName: string) => byBic(bic)?.name || bankName,
    [byBic],
  );

  const getBankLogo = useCallback(
    (bic: string | undefined, logoUrl: string) => byBic(bic)?.logoUrl || logoUrl,
    [byBic],
  );

  return { getBankDisplayName, getBankLogo };
}
